/**
 * ComingSoonOverlay — a friendly centered modal that explains why a locked
 * home-screen mode (Solo Grind, Run It Up, Leaderboard, Crew Wars) isn't
 * playable during the multiplayer beta, and nudges the player toward the live
 * tables that ARE open.
 *
 * Self-contained DOM/CSS (no GSAP, no 3D). Body-level singleton — call show()
 * with the tapped mode's details. Resolves when dismissed.
 */

const STYLE_ID = 'sdl-comingsoon-styles';

export interface ComingSoonOptions {
  /** Mode display name, e.g. "SOLO GRIND". */
  title: string;
  /** Mode icon URL (the same art used on the home card). */
  iconSrc: string;
  /** Accent colour for the glow + pill, e.g. "#00F5FF". */
  accent: string;
  /** One or two sentences describing the mode and when it opens. */
  message: string;
  /**
   * Optional callback for the primary "PLAY LIVE" button. When provided the
   * button shows and the overlay closes before invoking it (e.g. open the
   * lobby). When absent the primary button is hidden.
   */
  onPlayLive?: () => void;
}

export class ComingSoonOverlay {
  public element: HTMLElement;
  private cardEl: HTMLElement;
  private iconEl: HTMLImageElement;
  private titleEl: HTMLElement;
  private messageEl: HTMLElement;
  private playBtn: HTMLButtonElement;
  private closeHandler: (() => void) | null = null;

  constructor() {
    ComingSoonOverlay.injectStyles();
    this.element = document.createElement('div');
    this.element.className = 'sdl-comingsoon-host';
    this.element.innerHTML = `
      <div class="sdl-comingsoon-card" role="dialog" aria-modal="true">
        <button class="sdl-comingsoon-close" aria-label="Close">&times;</button>
        <div class="sdl-comingsoon-icon-wrap">
          <img class="sdl-comingsoon-icon" alt="" />
          <div class="sdl-comingsoon-lock">
            <img src="/assets/icons/ui/lock.svg" alt="Locked" />
          </div>
        </div>
        <span class="sdl-comingsoon-pill">Coming Soon</span>
        <h2 class="sdl-comingsoon-title"></h2>
        <p class="sdl-comingsoon-message"></p>
        <div class="sdl-comingsoon-actions">
          <button class="sdl-comingsoon-play">Play Live Now</button>
          <button class="sdl-comingsoon-dismiss">Got It</button>
        </div>
      </div>
    `;
    document.body.appendChild(this.element);

    this.cardEl = this.element.querySelector('.sdl-comingsoon-card') as HTMLElement;
    this.iconEl = this.element.querySelector('.sdl-comingsoon-icon') as HTMLImageElement;
    this.titleEl = this.element.querySelector('.sdl-comingsoon-title') as HTMLElement;
    this.messageEl = this.element.querySelector('.sdl-comingsoon-message') as HTMLElement;
    this.playBtn = this.element.querySelector('.sdl-comingsoon-play') as HTMLButtonElement;

    const dismissBtn = this.element.querySelector('.sdl-comingsoon-dismiss') as HTMLButtonElement;
    const closeBtn = this.element.querySelector('.sdl-comingsoon-close') as HTMLButtonElement;
    dismissBtn.addEventListener('click', () => this.close());
    closeBtn.addEventListener('click', () => this.close());
    // Backdrop tap closes; taps inside the card do not.
    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) this.close();
    });
  }

  /** Open the modal for a mode. Resolves once it has fully dismissed. */
  async show(opts: ComingSoonOptions): Promise<void> {
    this.iconEl.src = opts.iconSrc;
    this.titleEl.textContent = opts.title;
    this.messageEl.textContent = opts.message;
    this.cardEl.style.setProperty('--accent', opts.accent);

    if (opts.onPlayLive) {
      this.playBtn.style.display = '';
      this.playBtn.onclick = () => {
        const cb = opts.onPlayLive!;
        this.close().then(() => cb());
      };
    } else {
      this.playBtn.style.display = 'none';
      this.playBtn.onclick = null;
    }

    this.element.classList.add('visible');
    // Force reflow so the entrance transition fires from the hidden state.
    void this.cardEl.offsetWidth;
    this.cardEl.classList.add('in');

    return new Promise<void>((resolve) => {
      this.closeHandler = resolve;
    });
  }

  private close(): Promise<void> {
    this.cardEl.classList.remove('in');
    this.element.classList.remove('visible');
    return new Promise<void>((resolve) => {
      window.setTimeout(() => {
        const done = this.closeHandler;
        this.closeHandler = null;
        if (done) done();
        resolve();
      }, 220);
    });
  }

  destroy(): void {
    this.element.remove();
  }

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-comingsoon-host {
        position: fixed; inset: 0;
        z-index: 200;
        display: none;
        align-items: center; justify-content: center;
        padding: 24px;
        background: rgba(4, 4, 8, 0.72);
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
      }
      .sdl-comingsoon-host.visible { display: flex; }

      .sdl-comingsoon-card {
        --accent: #00F5FF;
        position: relative;
        display: flex; flex-direction: column; align-items: center;
        width: min(86vw, 360px);
        padding: 34px 26px 26px;
        background: linear-gradient(160deg, #16161e, #0c0c12);
        border: 1px solid rgba(255, 255, 255, 0.16);
        border: 1px solid color-mix(in srgb, var(--accent) 45%, transparent);
        border-radius: 22px;
        box-shadow: 0 22px 60px rgba(0, 0, 0, 0.7);
        box-shadow:
          0 22px 60px rgba(0, 0, 0, 0.7),
          0 0 40px color-mix(in srgb, var(--accent) 18%, transparent);
        text-align: center;
        transform: translateY(18px) scale(0.94);
        opacity: 0;
        transition: transform 0.26s cubic-bezier(0.34, 1.4, 0.6, 1), opacity 0.22s ease;
      }
      .sdl-comingsoon-card.in { transform: translateY(0) scale(1); opacity: 1; }

      .sdl-comingsoon-close {
        position: absolute; top: 12px; right: 14px;
        width: 30px; height: 30px;
        background: rgba(255, 255, 255, 0.06);
        border: none; border-radius: 50%;
        color: rgba(255, 255, 255, 0.55);
        font-size: 22px; line-height: 1;
        cursor: pointer;
        transition: background 0.2s, color 0.2s;
        -webkit-tap-highlight-color: transparent;
      }
      .sdl-comingsoon-close:active { background: rgba(255, 255, 255, 0.14); color: #fff; }

      .sdl-comingsoon-icon-wrap {
        position: relative;
        width: 88px; height: 88px;
        display: flex; align-items: center; justify-content: center;
        border-radius: 22px;
        background: radial-gradient(circle at 50% 40%, rgba(255, 255, 255, 0.08), transparent 70%);
        background: radial-gradient(circle at 50% 40%,
          color-mix(in srgb, var(--accent) 26%, transparent), transparent 70%);
        margin-bottom: 16px;
      }
      .sdl-comingsoon-icon {
        width: 52px; height: 52px;
        filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.45));
        opacity: 0.55;
      }
      .sdl-comingsoon-lock {
        position: absolute; right: -4px; bottom: -4px;
        width: 36px; height: 36px;
        display: flex; align-items: center; justify-content: center;
        background: var(--accent);
        border-radius: 50%;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
      }
      .sdl-comingsoon-lock img {
        width: 18px; height: 18px;
        filter: brightness(0) opacity(0.85);
      }

      .sdl-comingsoon-pill {
        font-family: 'Rajdhani', sans-serif;
        font-size: 12px; font-weight: 700;
        letter-spacing: 2px; text-transform: uppercase;
        color: var(--accent);
        padding: 4px 12px;
        border: 1px solid rgba(255, 255, 255, 0.22);
        border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
        border-radius: 999px;
        margin-bottom: 12px;
      }

      .sdl-comingsoon-title {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 30px; letter-spacing: 2px;
        color: #fff;
        margin: 0 0 10px;
      }

      .sdl-comingsoon-message {
        font-family: 'Rajdhani', sans-serif;
        font-size: 15px; font-weight: 500; line-height: 1.5;
        color: rgba(255, 255, 255, 0.74);
        margin: 0 0 22px;
        max-width: 280px;
      }

      .sdl-comingsoon-actions {
        display: flex; flex-direction: column; gap: 10px;
        width: 100%;
      }
      .sdl-comingsoon-play, .sdl-comingsoon-dismiss {
        width: 100%; padding: 13px 16px;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 18px; letter-spacing: 1.5px;
        border-radius: 12px;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        transition: transform 0.12s, filter 0.2s, background 0.2s;
      }
      .sdl-comingsoon-play {
        border: none;
        color: #08080A;
        background: var(--accent);
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
        box-shadow: 0 6px 18px color-mix(in srgb, var(--accent) 35%, transparent);
      }
      .sdl-comingsoon-play:active { transform: scale(0.97); filter: brightness(1.08); }
      .sdl-comingsoon-dismiss {
        border: 1px solid rgba(255, 255, 255, 0.14);
        background: transparent;
        color: rgba(255, 255, 255, 0.6);
      }
      .sdl-comingsoon-dismiss:active { transform: scale(0.97); background: rgba(255, 255, 255, 0.06); }
    `;
    document.head.appendChild(style);
  }
}
